import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Warning, CheckCircle, Info } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const DrugTestingPage = () => {
  const navigate = useNavigate();

  const testingMethods = [
    {
      id: 'fentanyl-strips',
      emoji: '🧪',
      title: 'Fentanyl Test Strips',
      description: 'Quick, cheap, and can save your life',
      steps: [
        'Dissolve a small amount of your substance in water',
        'Dip the strip for 15 seconds',
        'Wait 2-5 minutes for results',
        'ONE line = fentanyl detected ⚠️',
        'TWO lines = no fentanyl detected ✅'
      ],
      warning: 'Even a negative test doesn\'t mean 100% safe. Fentanyl can be unevenly mixed.',
      link: 'https://dancesafe.org/fentanyl-test-strips/'
    },
    {
      id: 'reagent-testing',
      emoji: '⚗️',
      title: 'Reagent Testing Kits',
      description: 'Color-change tests to identify substances',
      steps: [
        'Place a tiny sample on a ceramic plate',
        'Add 1-2 drops of reagent',
        'Watch the color change',
        'Compare to the color chart',
        'Different reagents test for different things'
      ],
      warning: 'Reagent tests identify what\'s IN your substance, not purity or dose.',
      link: 'https://dancesafe.org/testing-kit-instructions/'
    },
    {
      id: 'drug-checking',
      emoji: '🏥',
      title: 'Drug Checking Services',
      description: 'Professional lab-quality testing',
      steps: [
        'Find a local drug checking service',
        'Bring a small sample anonymously',
        'Get detailed results on what\'s in it',
        'No judgment, just information'
      ],
      warning: 'Not available everywhere yet, but expanding across Canada.',
      link: 'https://www.drugchecking.ca/'
    }
  ];

  const fentanylFacts = [
    {
      emoji: '☠️',
      fact: 'Fentanyl is 50-100x stronger than morphine',
      detail: 'A lethal dose can be as small as a few grains of salt'
    },
    {
      emoji: '👀',
      fact: 'You can\'t see, smell, or taste fentanyl',
      detail: 'It\'s invisible when mixed into other drugs'
    },
    {
      emoji: '💊',
      fact: 'Fentanyl is in more than just opioids',
      detail: 'It\'s been found in coke, meth, MDMA, and pressed pills'
    },
    {
      emoji: '🎲',
      fact: 'Hot spots happen',
      detail: 'Fentanyl doesn\'t mix evenly - one part of a batch can be deadly while another is fine'
    }
  ];

  const safetyTips = [
    {
      emoji: '🧪',
      tip: 'Always test your drugs',
      detail: 'Even from trusted sources - they might not know what\'s in it either'
    },
    {
      emoji: '👥',
      tip: 'Never use alone',
      detail: 'Have someone with you who can call for help or use Naloxone'
    },
    {
      emoji: '📦',
      tip: 'Carry Naloxone',
      detail: 'Free at pharmacies across Canada - it reverses opioid overdoses'
    },
    {
      emoji: '🐢',
      tip: 'Start low, go slow',
      detail: 'Take a small test dose and wait before taking more'
    },
    {
      emoji: '🚫',
      tip: 'Don\'t mix',
      detail: 'Combining substances increases overdose risk dramatically'
    },
    {
      emoji: '📱',
      tip: 'Use NORS',
      detail: 'Call 1-888-688-6677 for virtual safe consumption support 24/7'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">🔬</span>
              <h1 className="text-2xl font-bold text-gradient">
                Drug Testing & Fentanyl Safety
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* Hero */}
        <div className="mb-8 p-6 bg-gradient-to-br from-rose-500/20 to-red-500/10 rounded-3xl border border-rose-500/20">
          <div className="text-center">
            <div className="text-5xl mb-3">⚠️</div>
            <h2 className="text-2xl font-bold text-[rgb(var(--text-primary))] mb-3">
              Test your stuff, stay safe 💜
            </h2>
            <p className="text-[rgb(var(--text-secondary))] leading-relaxed">
              Fentanyl is everywhere now. Testing doesn't mean you're paranoid - it means you're smart. 
              No shame, just harm reduction.
            </p>
          </div>
        </div>

        {/* Fentanyl Facts */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-4 flex items-center">
            <Warning size={20} className="mr-2 text-[rgb(var(--danger))]" />
            Why Fentanyl is Different
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {fentanylFacts.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10"
              >
                <div className="text-2xl mb-2">{item.emoji}</div>
                <p className="font-semibold text-[rgb(var(--text-primary))] mb-1">{item.fact}</p>
                <p className="text-sm text-[rgb(var(--text-secondary))]">{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testing Methods */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-4 flex items-center">
            <CheckCircle size={20} className="mr-2 text-[rgb(var(--success))]" />
            How to Test Your Drugs
          </h3>
          <div className="space-y-4">
            {testingMethods.map((method) => (
              <div
                key={method.id}
                className="p-5 bg-[rgb(var(--surface))] rounded-2xl border border-white/10"
              >
                <div className="flex items-start gap-4">
                  <div className="text-4xl">{method.emoji}</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-[rgb(var(--text-primary))] text-lg mb-1">
                      {method.title}
                    </h4>
                    <p className="text-sm text-[rgb(var(--text-secondary))] mb-3">
                      {method.description}
                    </p>
                    <div className="space-y-2 mb-3">
                      {method.steps.map((step, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="text-[rgb(var(--primary))] font-bold text-sm">{index + 1}.</span>
                          <span className="text-sm text-[rgb(var(--text-secondary))]">{step}</span>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-[rgb(var(--warning))]/10 rounded-xl border border-[rgb(var(--warning))]/20 mb-3">
                      <p className="text-sm text-[rgb(var(--text-secondary))]">
                        <span className="font-semibold text-[rgb(var(--warning))]">Note:</span> {method.warning}
                      </p>
                    </div>
                    <a
                      href={method.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-sm text-[rgb(var(--primary))] hover:underline"
                    >
                      Learn more →
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Safety Tips */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-4 flex items-center">
            <Info size={20} className="mr-2 text-[rgb(var(--primary))]" />
            Stay Safer
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {safetyTips.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10 hover:border-[rgb(var(--primary))]/30 transition-colors"
              >
                <div className="text-2xl mb-2">{item.emoji}</div>
                <p className="font-semibold text-[rgb(var(--text-primary))] mb-1">{item.tip}</p>
                <p className="text-sm text-[rgb(var(--text-secondary))]">{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Where to Get Supplies */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-4">
            🛒 Where to Get Testing Supplies
          </h3>
          <div className="space-y-3">
            <a
              href="https://dancesafe.org/shop/"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10 hover:border-[rgb(var(--primary))]/30 transition-colors"
            >
              <div className="font-semibold text-[rgb(var(--text-primary))]">DanceSafe</div>
              <p className="text-sm text-[rgb(var(--text-secondary))]">Reagent kits + fentanyl strips shipped discreetly</p>
            </a>
            <a
              href="https://www.bccsu.ca/naloxone/"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10 hover:border-[rgb(var(--primary))]/30 transition-colors"
            >
              <div className="font-semibold text-[rgb(var(--text-primary))]">Free Naloxone</div>
              <p className="text-sm text-[rgb(var(--text-secondary))]">Available at pharmacies across Canada - no prescription needed</p>
            </a>
            <a
              href="https://maxottawa.ca/programs-and-services/tea-to-go/"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10 hover:border-[rgb(var(--primary))]/30 transition-colors"
            >
              <div className="font-semibold text-[rgb(var(--text-primary))]">Tea2Go (Ottawa)</div>
              <p className="text-sm text-[rgb(var(--text-secondary))]">Free harm reduction supplies including fentanyl strips</p>
            </a>
          </div>
        </section>

        {/* Emergency Info */}
        <div className="p-6 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-2xl">
          <h3 className="text-lg font-semibold text-[rgb(var(--danger))] mb-3">
            🚨 If Someone is Overdosing
          </h3>
          <div className="space-y-2 text-sm text-[rgb(var(--text-secondary))]">
            <p><strong>1.</strong> Call 911 immediately</p>
            <p><strong>2.</strong> Give Naloxone if available (it won't hurt if it's not an opioid OD)</p>
            <p><strong>3.</strong> Put them in recovery position (on their side)</p>
            <p><strong>4.</strong> Stay with them until help arrives</p>
            <p><strong>5.</strong> Good Samaritan laws protect you from drug charges when calling for help</p>
          </div>
          <div className="mt-4 p-3 bg-[rgb(var(--background))] rounded-xl">
            <p className="text-sm font-semibold text-[rgb(var(--text-primary))]">
              NORS Canada: 1-888-688-6677 (24/7 virtual safe consumption)
            </p>
          </div>
        </div>

        <div className="mt-8 p-4 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
          <p className="text-sm text-[rgb(var(--text-secondary))]">
            Testing your drugs isn't paranoid - it's smart. You deserve to party and stay alive. 💜
          </p>
        </div>
      </main>
    </div>
  );
};

export default DrugTestingPage;
